import styles from "../css/CollectionListScreen.module.css";
import { useEffect, useState } from "react";
import Collection from "../model/Collection";
import { useTranslation } from "react-i18next";
import CollectionListItemView, {
  getCollectionItemViewPositionUsingIndex,
} from "../view/CollectionListItemView";
import { handleErrorAlert } from "../../common/OnevocaServerError";
import {
  deleteCollection as deleteCollectionFromAPI,
  fetchCollectionList,
} from "../service/collectionAPI";
import LoadingScreen from "../../common/LoadingScreen";
import BigButton from "../../common/BigButton";
import CollectionAddScreen from "./CollectionAddScreen";
import Toast from "../../../components/ui/Toast";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import CollectionEditScreen from "./CollectionEditScreen";
import BasicMenu, { BasicMenuButton } from "../../common/BasicMenu";
import OnevocaDialog from "../../common/OnevocaDialog";
import ListEmptyText from "../../common/ListEmptyText";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface CollectionListScreenProps {
  open: boolean;
  showDefaultCollection: boolean;
  onSelectCollection: (collection: Collection) => void;
  onUpdateCollection: () => void;
  onClose: () => void;
}

export default function CollectionListScreen(props: CollectionListScreenProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();
  const [showLoading, setShowLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<Collection>();

  const [openCollectionAddScreen, setOpenCollectionAddScreen] = useState(false);
  const [openCollectionEditScreen, setOpenCollectionEditScreen] =
    useState(false);

  const [openMoreMenu, setOpenMoreMenu] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);

  const moreMenuButtons: BasicMenuButton[] = [
    {
      title: t("Edit"),
      handleOnClose: () => {
        setOpenMoreMenu(false);
        setOpenCollectionEditScreen(true);
      },
    },
    {
      title: t("Delete"),
      isWarning: true,
      handleOnClose: () => {
        setOpenMoreMenu(false);
        setOpenDeleteConfirmDialog(true);
      },
    },
  ];

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickCollectionMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    collection: Collection
  ) {
    setAnchorEl(event.currentTarget);
    setOpenMoreMenu(true);
    setSelectedCollection(collection);
  }

  async function deleteCollection(collection: Collection) {
    try {
      setShowLoading(true);
      await deleteCollectionFromAPI(collection.id);
      loadCollections();
      props.onUpdateCollection();
    } catch (error) {
      handleErrorAlert(error);
    } finally {
      setShowLoading(false);
    }
  }

  async function loadCollections() {
    try {
      setShowLoading(true);
      const newCollections = await fetchCollectionList(
        props.showDefaultCollection
      );
      setCollections(newCollections);
    } catch (error) {
      handleErrorAlert(error);
    } finally {
      setShowLoading(false);
    }
  }

  useEffect(() => {
    if (props.open === true) {
      loadCollections();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  if (props.open === false) {
    return null;
  }

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.main} onClick={props.onClose}>
          <div className={styles.container} onClick={handleOnClickContainer}>
            <div className={styles.contentContainer}>
              <ContainerNavigationView
                open={true}
                title={t("CollectionListNavTitle")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={props.onClose}
              />
              <div className={styles.listContainer}>
                {collections.length > 0 &&
                  collections.map((collection, index) => (
                    <CollectionListItemView
                      key={collection.id}
                      position={getCollectionItemViewPositionUsingIndex(
                        index,
                        collections
                      )}
                      collection={collection}
                      onSelectCollection={() => {
                        props.onClose();
                        props.onSelectCollection(collection);
                      }}
                      onClickMoreButton={(
                        e: React.MouseEvent<HTMLDivElement>
                      ) => {
                        handleOnClickCollectionMoreButton(e, collection);
                      }}
                    />
                  ))}
              </div>
            </div>
            {collections.length === 0 && (
              <div className={styles.listEmptyTextContainer}>
                <ListEmptyText text={t("CollectionListEmptyMessage")} />
              </div>
            )}
            <div className={styles.bottomContainer}>
              <BigButton
                title={t("CollectionAddNavTitle")}
                variant="special"
                handleClick={() => {
                  setOpenCollectionAddScreen(true);
                }}
              />
            </div>
          </div>
        </div>
        <CollectionAddScreen
          open={openCollectionAddScreen}
          onCreate={() => {
            loadCollections();
            setToastMessage(t("CollectionNewAddMessage"));
            props.onUpdateCollection();
          }}
          onClose={() => {
            setOpenCollectionAddScreen(false);
          }}
        />
        <CollectionEditScreen
          open={openCollectionEditScreen}
          collection={selectedCollection}
          onRename={() => {
            loadCollections();
            setToastMessage(t("TermEditedToastMessage"));
            props.onUpdateCollection();
          }}
          onClose={() => {
            setOpenCollectionEditScreen(false);
            setSelectedCollection(undefined);
          }}
        />
        <BasicMenu
          isOpen={openMoreMenu}
          anchorEl={anchorEl}
          buttons={moreMenuButtons}
          handleOnClose={() => {
            setOpenMoreMenu(false);
          }}
        />
        <Toast
          open={toastMessage !== null}
          message={toastMessage}
          onClose={() => {
            setToastMessage(null);
          }}
        />
        <OnevocaDialog
          open={openDeleteConfirmDialog}
          title={selectedCollection?.title ?? null}
          message={t("CollectionDeleteConfirmMessage")}
          buttons={[
            {
              title: t("Delete"),
              style: "delete",
              onClick: () => {
                if (selectedCollection !== undefined) {
                  deleteCollection(selectedCollection);
                  setSelectedCollection(undefined);
                }
                setOpenDeleteConfirmDialog(false);
              },
            },
            {
              title: t("Cancel"),
              style: "normal",
              onClick: () => {
                setOpenDeleteConfirmDialog(false);
                setSelectedCollection(undefined);
              },
            },
          ]}
        />
        {showLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
